import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ScanFace, Hand, PersonStanding, AudioLines } from "lucide-react";
import { RevealOnScroll } from "./RevealText";
import { liveDemoTabs } from "../data/staticData";
import { useLanguage } from "../i18n/LanguageContext";

const icons = { ScanFace, Hand, PersonStanding, AudioLines };

// Renders a real MediaPipe-derived landmark overlay (coordinates baked in
// staticData.js — see /app/scripts/generate_landmarks.py). Dense tesselation
// (face only) is rendered as static, non-animated lines for performance;
// only the highlighted contours/connections and points pulse via CSS
// keyframe animations (GPU-composited, cheap even with 100+ elements).
function LandmarkOverlay({ tab }) {
  const meshLines = tab.meshLines || [];
  const mainLines = tab.contourLines || tab.lines || [];
  const points = tab.points || [];

  return (
    <svg viewBox={`0 0 100 ${tab.viewBoxH}`} preserveAspectRatio="xMidYMid slice" className="absolute inset-0 w-full h-full pointer-events-none">
      {meshLines.length > 0 && (
        <g stroke={tab.color} strokeWidth="0.12" opacity="0.32">
          {meshLines.map(([x1, y1, x2, y2], i) => (
            <line key={`${tab.id}-mesh-${i}`} x1={x1} y1={y1} x2={x2} y2={y2} />
          ))}
        </g>
      )}
      {mainLines.map(([x1, y1, x2, y2], i) => (
        <line
          key={`${tab.id}-line-${i}`}
          x1={x1} y1={y1} x2={x2} y2={y2}
          stroke={tab.color}
          strokeWidth="0.3"
          className="mesh-line-pulse"
          style={{ filter: `drop-shadow(0 0 2px ${tab.color}aa)`, animationDelay: `${(i % 12) * 0.12}s` }}
        />
      ))}
      {points.map(([x, y], i) => (
        <circle
          key={`${tab.id}-pt-${i}`}
          cx={x} cy={y} r="0.5"
          fill={tab.color}
          className="mesh-dot-pulse"
          style={{ filter: `drop-shadow(0 0 3px ${tab.color})`, animationDelay: `${(i % 10) * 0.15}s` }}
        />
      ))}
    </svg>
  );
}

// Voice Analysis has no photo/landmark overlay — instead renders a live
// audio-waveform visual (bars bouncing like a real equalizer) to match the
// "listening" nature of acoustic marker analysis.
function VoiceWaveform() {
  const bars = Array.from({ length: 44 }, (_, i) => i);
  return (
    <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-[#0a1012] to-[#050505]">
      <div className="absolute inset-0 grid-line opacity-25" />
      <div className="absolute w-[420px] h-[420px] rounded-full bg-[#00F0FF]/10 blur-[110px]" />
      <div className="relative flex items-end gap-[4px] h-36 sm:h-44">
        {bars.map((i) => {
          const heightPct = 18 + Math.abs(Math.sin(i * 0.55)) * 78;
          return (
            <span
              key={i}
              className="w-[4px] rounded-full bg-[#00F0FF] voice-bar-pulse"
              style={{
                height: `${heightPct}%`,
                filter: "drop-shadow(0 0 4px rgba(0,240,255,0.6))",
                animationDelay: `${(i % 14) * 0.08}s`,
                animationDuration: `${1 + (i % 5) * 0.14}s`,
              }}
            />
          );
        })}
      </div>
    </div>
  );
}

export function LiveDemo() {
  const { t } = useLanguage();
  const l = t.liveDemo;
  const [activeId, setActiveId] = useState(liveDemoTabs[0].id);
  const activeTab = liveDemoTabs.find((tb) => tb.id === activeId);
  const activeCopy = l.tabs[activeId];

  return (
    <section id="live-demo" className="relative py-24 lg:py-32 bg-[#050505] overflow-hidden" data-testid="live-demo-section">
      <div className="absolute inset-0 grid-line opacity-30" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <RevealOnScroll className="max-w-2xl mb-16">
          <span className="font-mono-data text-xs text-[#00F0FF]">/ {l.eyebrow}</span>
          <h2 className="mt-4 font-display font-bold text-4xl sm:text-5xl text-white tracking-tighter">
            {l.title}
          </h2>
          <p className="mt-4 text-zinc-400 text-base sm:text-lg">{l.subtitle}</p>
        </RevealOnScroll>

        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <RevealOnScroll delay={0.1} className="lg:col-span-5 flex flex-col gap-3">
            {liveDemoTabs.map((tab) => {
              const Icon = icons[tab.icon];
              const copy = l.tabs[tab.id];
              const isActive = tab.id === activeId;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveId(tab.id)}
                  data-testid={`live-demo-tab-${tab.id}`}
                  className={`text-left rounded-xl border p-5 transition-all duration-300 ${
                    isActive
                      ? "border-[#00F0FF]/40 bg-[#00F0FF]/[0.06]"
                      : "border-white/10 bg-transparent hover:border-white/25"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon className={`w-5 h-5 shrink-0 ${isActive ? "text-[#00F0FF]" : "text-zinc-500"}`} strokeWidth={1.5} />
                    <span className={`font-display font-semibold text-base ${isActive ? "text-white" : "text-zinc-300"}`}>
                      {copy.label}
                    </span>
                    <span className="ml-auto font-mono-data text-[10px] text-zinc-500">{copy.metric}</span>
                  </div>
                  <p className="mt-2 text-sm text-zinc-500 leading-relaxed">{copy.description}</p>

                  <AnimatePresence>
                    {isActive && (
                      <motion.p
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3 }}
                        data-testid="live-demo-detail"
                        className="mt-3 pt-3 border-t border-white/10 text-xs text-zinc-400 leading-relaxed overflow-hidden"
                      >
                        <span className="text-[#00F0FF] font-mono-data uppercase mr-1.5">{l.whatAiSeesLabel}:</span>
                        {copy.detail}
                      </motion.p>
                    )}
                  </AnimatePresence>
                </button>
              );
            })}
          </RevealOnScroll>

          <RevealOnScroll delay={0.2} className="lg:col-span-7">
            <div
              className="relative rounded-2xl overflow-hidden border border-white/10 h-[420px] sm:h-[480px]"
              data-testid="live-demo-visual"
            >
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.4 }}
                  className="absolute inset-0"
                >
                  {activeTab.image ? (
                    <>
                      <img
                        src={activeTab.image}
                        alt={activeCopy.label}
                        className="w-full h-full object-cover"
                        style={{ filter: "saturate(0.4) contrast(1.1) brightness(0.85)" }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-black/40" />
                      <div className="absolute inset-0 scan-lines" />
                      <LandmarkOverlay tab={activeTab} />
                    </>
                  ) : (
                    <VoiceWaveform />
                  )}

                  <div className="absolute top-5 left-5 font-mono-data text-[10px] text-[#00F0FF] px-2 py-1 rounded border border-[#00F0FF]/30 bg-black/60">
                    {activeCopy.metric.toUpperCase()}
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </RevealOnScroll>
        </div>
      </div>
    </section>
  );
}
